import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import * as Bixby from '$/core';
import { ClientAdminService, CompanyService } from '$/services';
import InputBlock from '$/components/global/inputBlock';
import { addClientCompany, deleteClientCompany } from '$/store/company/actions';
import BixButton from '$/components/global/bixButton';
import './ClientCompanyAccess.scss';
import Modal from '$/components/global/modal';
import {
  getIsCurrentUserBixbyComplianceAdminState,
  getIsCurrentUserCompanyMNPIInfoAdminState,
  getIsCurrentUserCompanyPublicInfoAdminState,
  getIsCurrentUserSuperAdminState,
} from '$/store/profile/selectors';
import { reloadCurrentClient } from '$/store/client/actions';
import { toastSuccess, toastError } from '$/store/toast/actions';
import { ColumnApi, GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import ForbiddenCharFilter from '$/components/global/floatingFilters/ForbiddenCharFilter';
import DeleteButton from '$/components/grid/DeleteButton';
import Loading from '$/components/loading/Loading';
import classnames from 'classnames';
import {
  getBixbyComplianceFeatureFlagState,
  getCloPermissioningEnabled,
} from '$/store/release-feature-flags/selectors';
import BulkImportCompanies from '$/pages/admin/client/client-detail/bulk-import-companies';
import { ClientCompayBasisOfAccess } from '$/core/enums';

interface IClientCompanyAccessProps {
  client?: Bixby.Models.Client;
  isSuperAdmin: boolean;
  isLoading: boolean;
  isBixbyComplianceAdmin: boolean;
  isCompanyMNPIInfoAdmin: boolean;
  isCompanyPublicInfoAdmin: boolean;
  isBixbyComplianceEnabled: boolean;
  isCloPermissioningEnabled: boolean;
  dispatchAddClientCompany: (clientId: string, companyId: string) => Promise<void>;
  dispatchDeleteClientCompany: (clientId: string, companyId: string) => Promise<void>;
  dispatchToastSuccess: (message: string) => Promise<void>;
  dispatchToastError: (message: string) => Promise<void>;
  dispatchReloadCurrentClient: () => Promise<void>;
}

interface DeleteCompanyModal {
  active: boolean;
  companyId: string | undefined;
  companyName: string | undefined;
}

function CompanyAccessGrid({
  clientCompanies,
  onGridReady,
  getContextMenuItems,
  deleteButtonCellRenderer,
  clientName,
  useMnpiCompanies,
  isBixbyComplianceAdmin,
  isBixbyComplianceEnabled,
  isCloPermissioningEnabled,
}: {
  clientCompanies: Bixby.Models.ClientCompany[] | undefined;
  onGridReady: (params: any) => void;
  getContextMenuItems: () => string[];
  deleteButtonCellRenderer: (props: any) => JSX.Element;
  clientName: string;
  useMnpiCompanies?: boolean;
  isBixbyComplianceAdmin: boolean;
  isBixbyComplianceEnabled: boolean;
  isCloPermissioningEnabled: boolean;
}) {
  const hideRemoval: boolean = isBixbyComplianceEnabled && !isBixbyComplianceAdmin;

  return (
    <div className="spreadsheet">
      {clientCompanies && !Bixby.Utils.isNullOrEmpty(clientCompanies) ? (
        <div className="client-company-access-data-grid">
          <div className="grid_container">
            <div className="grid ag-theme-alpine">
              <AgGridReact
                defaultColDef={{
                  flex: 1,
                  minWidth: 150,
                  resizable: true,
                  sortable: true,
                  lockPinned: true,
                  filter: 'agTextColumnFilter',
                }}
                onGridReady={onGridReady}
                rowData={clientCompanies}
                suppressDragLeaveHidesColumns={true}
                suppressClipboardPaste={true}
                suppressMovableColumns={true}
                getContextMenuItems={getContextMenuItems}
                animateRows={true}
                headerHeight={32}
                rowHeight={32}
                components={{ forbiddenCharFilter: ForbiddenCharFilter }}
                columnDefs={[
                  {
                    maxWidth: 60,
                    sortable: false,
                    suppressMenu: true,
                    floatingFilter: false,
                    suppressColumnsToolPanel: true,
                    cellRenderer: deleteButtonCellRenderer,
                    headerName: '',
                    field: 'id',
                    hide: hideRemoval,
                  },
                  {
                    field: 'uniqueName',
                    headerName: 'Company Name',
                    minWidth: 250,
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                    floatingFilterComponent: 'forbiddenCharFilter',
                    cellRenderer: (params: ICellRendererParams) => {
                      return (
                        <Link className="bix-link link_underline" to={`/admin/company/${params.data['company_Id']}`}>
                          {params.value}
                        </Link>
                      );
                    },
                  },
                  {
                    field: 'firstAccessDate',
                    headerName: 'Date of Access',
                    minWidth: 150,
                    maxWidth: 175,
                    cellRenderer: (params: ICellRendererParams) => {
                      return params.value ? new Date(params.value).toLocaleDateString() : 'Access Pending';
                    },
                  },
                  {
                    field: 'isActive',
                    headerName: 'Is Active?',
                    minWidth: 125,
                    maxWidth: 150,
                    valueFormatter: (params: any) => {
                      return params.value ? 'Yes' : 'No';
                    },
                  },
                  {
                    field: 'basisOfAccess',
                    headerName: 'Basis of Access',
                    minWidth: 125,
                    maxWidth: 150,
                    hide: !isCloPermissioningEnabled,
                    valueFormatter: (params: any) => {
                      if (params.value === ClientCompayBasisOfAccess.CloHoldingsAccess) {
                        return 'CLO Holdings';
                      }

                      if (params.value === ClientCompayBasisOfAccess.DataSiteAccess) {
                        return 'Data Site';
                      }

                      return '';
                    },
                  },
                ]}></AgGridReact>
            </div>
          </div>
        </div>
      ) : (
        <p className="space_around">{`There are no${
          useMnpiCompanies ? ' MNPI' : ' non-MNPI'
        } companies associated with ${clientName}.`}</p>
      )}
    </div>
  );
}

function AddCompany({
  handleCompanyChange,
  getExactMatchDataList,
  fillDataListIfEmpty,
  filteredCompanies,
  company,
  handleOpenConfirmAddCompany,
  addCompanyModalActive,
  handleCancelAddCompany,
  handleAddCompany,
  client,
}) {
  const [bulkImportModal, setBulkImportModal] = useState(false);

  const handleBulkImportModal = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    setBulkImportModal(true);
  };

  const handleBulkImportModalDismiss = () => {
    setBulkImportModal(false);
  };

  return (
    <div className="add_company">
      <h3>Add a Company</h3>
      <InputBlock title={`Associate an existing company with ${client.name}`}>
        <input
          list="companies"
          name="company"
          onChange={handleCompanyChange}
          onInput={getExactMatchDataList}
          onFocus={fillDataListIfEmpty}
          autoComplete="off"
        />
        <datalist id="companies">
          {filteredCompanies.map((result, ix) => {
            return (
              <option key={ix} label={result.bixbyId + '-' + result.name} value={result.bixbyId}>
                {result.bixbyId}
              </option>
            );
          })}
        </datalist>
      </InputBlock>
      <BixButton type="button" disabled={!Bixby.Utils.isDefined(company)} onClick={handleOpenConfirmAddCompany}>
        Notify Client of Access
      </BixButton>
      <BixButton type="button" onClick={handleBulkImportModal}>
        Upload File
      </BixButton>
      <BulkImportCompanies
        clientId={client.id}
        active={bulkImportModal}
        onImportSubmit={handleBulkImportModalDismiss} // close modal on submit
        onCloseModal={handleBulkImportModalDismiss}
      />

      <Modal title="Notify Client of Access" active={addCompanyModalActive} onClose={handleCancelAddCompany}>
        <p>
          Allow {client.name} to access {(company || ({} as any)).name}?
        </p>
        <p className="bixby_id">{(company || ({} as any)).bixbyId}</p>
        <div className="button_side_wrap">
          <BixButton type="button" onClick={handleAddCompany}>
            Add {(company || ({} as any)).name}
          </BixButton>
          <BixButton light type="button" onClick={handleCancelAddCompany}>
            Cancel
          </BixButton>
        </div>
      </Modal>
    </div>
  );
}

const ClientCompanyAccess: React.FC<IClientCompanyAccessProps> = props => {
  const {
    client,
    dispatchAddClientCompany,
    dispatchDeleteClientCompany,
    isSuperAdmin,
    isLoading,
    dispatchToastError,
    dispatchToastSuccess,
    dispatchReloadCurrentClient,
    isBixbyComplianceAdmin,
    isCompanyMNPIInfoAdmin,
    isCompanyPublicInfoAdmin,
    isBixbyComplianceEnabled,
    isCloPermissioningEnabled,
  } = props;
  const [companies, setCompanies] = useState([] as Bixby.Models.AdminCompanyForClientAdd[]);
  const [orderedCompanies, setOrderedCompanies] = useState([] as Bixby.Models.AdminCompanyForClientAdd[]);
  const [company, setCompany] = useState(undefined as Bixby.Models.AdminCompanyForClientAdd | undefined);
  const [addCompanyModalActive, setAddCompanyModalActive] = useState(false);
  const [deleteCompanyModal, setDeleteCompanyModal] = useState(undefined as DeleteCompanyModal | undefined);
  const [isSyncingPublicCompanies, setIsSyncingPublicCompanies] = useState(false);
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [filteredCompanies, setfilteredCompanies] = useState([] as Bixby.Models.AdminCompanyForClientAdd[]);

  const clientCompanies = useMemo(
    () => (client && client.companies ? client.companies.filter(_ => !_.isSECPublic) : undefined),
    [client]
  );

  useEffect(() => {
    const getCompanies = async () => {
      if (isSuperAdmin && client && !client.allowCreditSightsClientRoles) {
        const results = await CompanyService.getAllForCompanyAdd();
        setCompanies(results);
      }
    };
    getCompanies();
  }, [isSuperAdmin, client]);

  useEffect(() => {
    const ordered: Bixby.Models.AdminCompanyForClientAdd[] = Bixby.Utils.orderBy(c => c.name);
    setOrderedCompanies(ordered);
  }, [companies]);

  const handleAddCompany = async () => {
    if (client && company) {
      await dispatchAddClientCompany(client.id, company.id);
    }
    setAddCompanyModalActive(false);
  };

  const handleOpenConfirmAddCompany = () => {
    setAddCompanyModalActive(true);
  };

  const handleCancelAddCompany = () => {
    setAddCompanyModalActive(false);
  };

  const handleCompanyChange = (ev: React.FormEvent<HTMLInputElement>) => {
    const selected = (companies || []).find(c => c.bixbyId === ev.currentTarget.value);
    setCompany(selected);
  };

  const handleDeleteCompany = async () => {
    if (client && deleteCompanyModal?.companyId) {
      await dispatchDeleteClientCompany(client.id, deleteCompanyModal.companyId);
    }

    setDeleteCompanyModal({
      active: false,
      companyId: undefined,
      companyName: undefined,
    });
  };

  const handleOpenConfirmDeleteCompany = (row: Bixby.Models.ClientCompany) => {
    setDeleteCompanyModal({
      active: true,
      companyId: row.company_Id,
      companyName: row.company_Name,
    });

    return Promise.resolve();
  };

  const handleCancelDeleteCompany = () => {
    setDeleteCompanyModal({
      active: false,
      companyId: undefined,
      companyName: undefined,
    });
  };

  const handleDisablePublicCompanies = async () => {
    if (!client) return;

    try {
      setIsSyncingPublicCompanies(true);
      await ClientAdminService.disablePublicCompanies(client.id);
      await dispatchReloadCurrentClient();
      dispatchToastSuccess('Disabled public companies.');
    } catch (err: any) {
      dispatchToastError('Failed to disable public companies.');
    } finally {
      setIsSyncingPublicCompanies(false);
    }
  };

  const handleEnablePublicCompanies = async () => {
    if (!client) return;

    try {
      setIsSyncingPublicCompanies(true);
      await ClientAdminService.enablePublicCompanies(client.id);
      await dispatchReloadCurrentClient();
      dispatchToastSuccess('Enabled public companies.');
    } catch (err: any) {
      dispatchToastError('Failed to enable public companies.');
    } finally {
      setIsSyncingPublicCompanies(false);
    }
  };

  const deleteButtonCellRenderer = (props: any) => {
    const handleDeleteButton = () => {
      handleOpenConfirmDeleteCompany(props.data);
      return Promise.resolve();
    };

    return <DeleteButton row={props.data} deleter={handleDeleteButton} />;
  };

  const getExactMatchDataList = (evt: any) => {
    const input = evt.target.value;
    const filtered: Bixby.Models.AdminCompanyForClientAdd[] | [] =
      orderedCompanies.filter(c => c.bixbyId === input) || orderedCompanies;

    setfilteredCompanies(filtered);
  };
  const fillDataListIfEmpty = (evt: any) => {
    const input = evt.target.value;

    if (!input) {
      setfilteredCompanies(orderedCompanies);
    }
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'expandAll', 'contractAll', 'separator', 'export'];
    return result;
  };

  const category1ClientCompanies = useMemo(
    () =>
      (!isBixbyComplianceEnabled || isBixbyComplianceAdmin || isCompanyPublicInfoAdmin) && clientCompanies
        ? clientCompanies?.filter(_ => _.declarationType === Bixby.Enums.DeclarationType.Public)
        : undefined,
    [clientCompanies, isBixbyComplianceEnabled, isBixbyComplianceAdmin, isCompanyPublicInfoAdmin]
  );

  const category2ClientCompanies = useMemo(
    () =>
      isBixbyComplianceEnabled && (isBixbyComplianceAdmin || isCompanyMNPIInfoAdmin) && clientCompanies
        ? clientCompanies.filter(_ => _.declarationType === Bixby.Enums.DeclarationType.Private)
        : undefined,
    [clientCompanies, isBixbyComplianceEnabled, isBixbyComplianceAdmin, isCompanyMNPIInfoAdmin]
  );

  // The full company list is required if you are a super admin and the client in question does not allow CreditSights client roles
  if (!client || (!companies && isSuperAdmin && !client.allowCreditSightsClientRoles)) {
    return null;
  } else if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return client && clientCompanies ? (
      <>
        {
          // Compliance Enabled
          // Individual client company relationships will not happen for clients which allow CreditSights client roles (only SEC public companies)
          !client.allowCreditSightsClientRoles && isBixbyComplianceEnabled && (
            <section className="client_datasiteaccess">
              {/* Only Bixby Compliance Admins and Public Info Admins who are super admins can add client companies */}
              {(isBixbyComplianceAdmin || isCompanyPublicInfoAdmin) && isSuperAdmin && (
                <AddCompany
                  handleCompanyChange={handleCompanyChange}
                  getExactMatchDataList={getExactMatchDataList}
                  fillDataListIfEmpty={fillDataListIfEmpty}
                  filteredCompanies={filteredCompanies}
                  company={company}
                  handleOpenConfirmAddCompany={handleOpenConfirmAddCompany}
                  addCompanyModalActive={addCompanyModalActive}
                  handleCancelAddCompany={handleCancelAddCompany}
                  handleAddCompany={handleAddCompany}
                  client={client}
                />
              )}
              <div
                className={classnames('client_datasiteaccess_grid', {
                  super_admin: !isBixbyComplianceAdmin && isSuperAdmin,
                  admin: !isBixbyComplianceAdmin && !isSuperAdmin,
                })}>
                {(isBixbyComplianceAdmin || isCompanyPublicInfoAdmin) && (
                  <div>
                    <h3>Companies</h3>
                    <CompanyAccessGrid
                      clientCompanies={category1ClientCompanies}
                      onGridReady={onGridReady}
                      getContextMenuItems={getContextMenuItems}
                      deleteButtonCellRenderer={deleteButtonCellRenderer}
                      clientName={client.name}
                      isBixbyComplianceAdmin={isBixbyComplianceAdmin}
                      isBixbyComplianceEnabled={isBixbyComplianceEnabled}
                      isCloPermissioningEnabled={isCloPermissioningEnabled}
                    />
                  </div>
                )}
                {(isBixbyComplianceAdmin || isCompanyMNPIInfoAdmin) && (
                  <div>
                    <h3>MNPI Companies</h3>
                    <CompanyAccessGrid
                      clientCompanies={category2ClientCompanies}
                      onGridReady={onGridReady}
                      getContextMenuItems={getContextMenuItems}
                      deleteButtonCellRenderer={deleteButtonCellRenderer}
                      clientName={client.name}
                      useMnpiCompanies
                      isBixbyComplianceAdmin={isBixbyComplianceAdmin}
                      isBixbyComplianceEnabled={isBixbyComplianceEnabled}
                      isCloPermissioningEnabled={isCloPermissioningEnabled}
                    />
                  </div>
                )}
                {/* Only Bixby Compliance Admins can remove client companies */}
                {isBixbyComplianceAdmin && (
                  <Modal
                    title="Remove Company Access"
                    active={(deleteCompanyModal || ({} as DeleteCompanyModal)).active || false}
                    onClose={handleCancelDeleteCompany}>
                    <p>Remove access to company {(deleteCompanyModal || ({} as DeleteCompanyModal)).companyName}?</p>
                    <div className="button_side_wrap">
                      <BixButton type="button" onClick={handleDeleteCompany}>
                        Remove {(deleteCompanyModal || ({} as DeleteCompanyModal)).companyName}
                      </BixButton>
                      <BixButton light type="button" onClick={handleCancelDeleteCompany}>
                        Cancel
                      </BixButton>
                    </div>
                  </Modal>
                )}
              </div>
            </section>
          )
        }

        {
          // Compliance Not Enabled
          // Individual client company relationships will not happen for clients which allow CreditSights client roles (only SEC public companies)
          !client.allowCreditSightsClientRoles && !isBixbyComplianceEnabled && (
            <section className="client_datasiteaccess">
              <div
                className={classnames('client_datasiteaccess_grid', {
                  super_admin: isSuperAdmin,
                  admin: !isSuperAdmin,
                })}>
                <div>
                  <h3>Companies</h3>
                  <CompanyAccessGrid
                    clientCompanies={category1ClientCompanies}
                    onGridReady={onGridReady}
                    getContextMenuItems={getContextMenuItems}
                    deleteButtonCellRenderer={deleteButtonCellRenderer}
                    clientName={client.name}
                    isBixbyComplianceAdmin={isBixbyComplianceAdmin}
                    isBixbyComplianceEnabled={isBixbyComplianceEnabled}
                    isCloPermissioningEnabled={isCloPermissioningEnabled}
                  />
                </div>
                {isSuperAdmin && (
                  <AddCompany
                    handleCompanyChange={handleCompanyChange}
                    getExactMatchDataList={getExactMatchDataList}
                    fillDataListIfEmpty={fillDataListIfEmpty}
                    filteredCompanies={filteredCompanies}
                    company={company}
                    handleOpenConfirmAddCompany={handleOpenConfirmAddCompany}
                    addCompanyModalActive={addCompanyModalActive}
                    handleCancelAddCompany={handleCancelAddCompany}
                    handleAddCompany={handleAddCompany}
                    client={client}
                  />
                )}
                <Modal
                  title="Remove Company Access"
                  active={(deleteCompanyModal || ({} as DeleteCompanyModal)).active || false}
                  onClose={handleCancelDeleteCompany}>
                  <p>Remove access to company {(deleteCompanyModal || ({} as DeleteCompanyModal)).companyName}?</p>
                  <div className="button_side_wrap">
                    <BixButton type="button" onClick={handleDeleteCompany}>
                      Remove {(deleteCompanyModal || ({} as DeleteCompanyModal)).companyName}
                    </BixButton>
                    <BixButton light type="button" onClick={handleCancelDeleteCompany}>
                      Cancel
                    </BixButton>
                  </div>
                </Modal>
              </div>
            </section>
          )
        }

        {isSuperAdmin && (
          <section className="public_company_access">
            <h3>Public Company Access</h3>
            {client.processingPublicCompanies && <p>Processing public companies...</p>}
            {!client.processingPublicCompanies && (
              <>
                {client.hasPublicCompanies && (
                  <BixButton useLoading isLoading={isSyncingPublicCompanies} onClick={handleDisablePublicCompanies}>
                    Disable Public Companies
                  </BixButton>
                )}
                {!client.hasPublicCompanies && (
                  <BixButton useLoading isLoading={isSyncingPublicCompanies} onClick={handleEnablePublicCompanies}>
                    Enable Public Companies
                  </BixButton>
                )}
              </>
            )}
          </section>
        )}
      </>
    ) : (
      <></>
    );
  }
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchAddClientCompany: (clientId: string, companyId: string) =>
    dispatch(addClientCompany(clientId, companyId, () => dispatch(reloadCurrentClient()))),
  dispatchDeleteClientCompany: (clientId: string, companyId: string) =>
    dispatch(deleteClientCompany(clientId, companyId, () => dispatch(reloadCurrentClient()))),
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
  dispatchReloadCurrentClient: () => dispatch(reloadCurrentClient()),
});
const mapStateToProps = (state: AppState) => {
  const { isSuperAdmin } = getIsCurrentUserSuperAdminState(state);
  const { isBixbyComplianceAdmin } = getIsCurrentUserBixbyComplianceAdminState(state);
  const { isCompanyMNPIInfoAdmin } = getIsCurrentUserCompanyMNPIInfoAdminState(state);
  const { isCompanyPublicInfoAdmin } = getIsCurrentUserCompanyPublicInfoAdminState(state);
  const { isBixbyComplianceEnabled } = getBixbyComplianceFeatureFlagState(state);
  const { isCloPermissioningEnabled } = getCloPermissioningEnabled(state);

  return {
    isSuperAdmin,
    isBixbyComplianceAdmin,
    isCompanyMNPIInfoAdmin,
    isCompanyPublicInfoAdmin,
    isBixbyComplianceEnabled,
    isCloPermissioningEnabled,
    client: state.clientState.currentClient,
    isLoading: state.clientState.isLoading || state.companyState.isLoading,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ClientCompanyAccess);
